import type { EnrichmentProblem } from '@/stores/contactStore'

/**
 * Turning the API's classified enrichment problems into what a panel shows.
 *
 * The classification itself lives on the server, which is the only place that
 * sees a vendor's raw error text. These functions decide presentation only: what
 * to lead with, what to colour as a fault, and whether to say anything at all.
 *
 * The distinction that matters is `actionable`. "Hunter has no API key" is
 * something the person reading can fix in a minute; "Hunter had nothing to
 * search with for this contact" is not a fault at all and must not be shown as
 * one. Before this the UI said "failed" for every case alike, so nobody could
 * tell whether to go and change a key.
 */

/** Problems the user can act on, which is what a banner should lead with. */
export function actionableProblems(problems?: EnrichmentProblem[]): EnrichmentProblem[] {
  return (problems ?? []).filter((p) => p.actionable)
}

/** Entries that are genuinely something going wrong, as opposed to a provider
 *  reporting it had no input or is not on the plan. */
export function realProblems(problems?: EnrichmentProblem[]): EnrichmentProblem[] {
  return (problems ?? []).filter((p) => p.is_problem !== false)
}

/** Entries that explain rather than alarm: no input, not on the plan. */
export function informationalProblems(problems?: EnrichmentProblem[]): EnrichmentProblem[] {
  return (problems ?? []).filter((p) => p.is_problem === false)
}

/**
 * The one line to put at the top, or null when there is nothing worth saying.
 *
 * Prefers what can be fixed. Two providers with the same complaint are one
 * sentence each, not a repeated one, and the order the API sent is kept because
 * it already put the actionable entries first.
 */
export function headlineMessage(problems?: EnrichmentProblem[]): string | null {
  const actionable = actionableProblems(problems)
  if (actionable.length) {
    return Array.from(new Set(actionable.map((p) => p.hint))).join(' ')
  }
  const real = realProblems(problems)
  if (real.length) return real[0].hint
  // Everything was informational -- every provider simply had nothing to work
  // with. Still worth saying: the user asked for enrichment and got little
  // back, and this is the sentence that explains why. Returning null here left
  // `problemTone` reporting 'info' with nothing to show, so the panel rendered
  // an empty box.
  const [first] = problems ?? []
  return first ? first.hint : null
}

/**
 * How prominent the banner should be.
 *
 * `action` when the reader can fix it, `warning` when something failed but not
 * at their hand, `info` when every entry is a provider explaining it had
 * nothing to do. An informational run must never be painted red: that is what
 * made a contact with no company website look broken.
 */
export function problemTone(problems?: EnrichmentProblem[]): 'action' | 'warning' | 'info' | null {
  if (!problems?.length) return null
  if (actionableProblems(problems).length) return 'action'
  if (realProblems(problems).length) return 'warning'
  return 'info'
}

/** Provider names read better capitalised, and `sec_edgar` is not a word. */
export function providerLabel(provider: string): string {
  if (provider === 'run') return 'Enrichment'
  return provider
    .split(/[_-]/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}
